import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, gradeColor } from "../lib/helpers";
import { Badge } from "./ui/badge";
import { Medal, TrendUp, TrendDown, Minus, ChartLineUp } from "@phosphor-icons/react";

const GRADE_STEPS = [
  { p: 4.3, l: "A+" }, { p: 4.0, l: "A" }, { p: 3.7, l: "A-" },
  { p: 3.3, l: "B+" }, { p: 3.0, l: "B" }, { p: 2.7, l: "B-" },
  { p: 2.3, l: "C+" }, { p: 2.0, l: "C" }, { p: 1.7, l: "C-" },
  { p: 1.3, l: "D+" }, { p: 1.0, l: "D" }, { p: 0.7, l: "D-" }, { p: 0.0, l: "F" },
];
const pointsToLetter = (p) => GRADE_STEPS.reduce((best, s) => (Math.abs(s.p - p) < Math.abs(best.p - p) ? s : best)).l;

function TrendTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-[#E9DDC6] bg-white px-3 py-2 shadow-premium text-xs">
      <p className="font-medium text-[#2C2A28]">{d.title}</p>
      <p className="text-[#8A8782]">{fmtDate(d.date)}</p>
      <p className="mt-1"><span className={`inline-block px-1.5 rounded border ${gradeColor(d.grade)}`}>{d.grade}</span></p>
    </div>
  );
}

export default function GradePanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    client.get(`/photographers/${user.id}/grade`)
      .then((r) => setData(r.data))
      .catch(() => setData({ overall_letter: null, overall_points: null, graded_count: 0, shoots: [] }));
  }, [user.id]);

  if (!data) return <p className="text-[#5C5A58]" data-testid="grade-loading">Loading your grades…</p>;

  // Oldest → newest for the trend line.
  const chrono = [...data.shoots].reverse();
  const chart = chrono.map((s, i) => ({ n: i + 1, points: s.points, grade: s.grade, title: s.title, date: s.shoot_date }));

  // Simple direction: latest grade vs overall average.
  let trend = null;
  if (data.graded_count >= 2 && data.overall_points != null) {
    const latest = chrono[chrono.length - 1].points;
    const diff = latest - data.overall_points;
    trend = diff > 0.15 ? "up" : diff < -0.15 ? "down" : "steady";
  }
  const trendMeta = {
    up: { icon: TrendUp, cls: "text-green-700", label: "Trending up — your latest work is above your average" },
    down: { icon: TrendDown, cls: "text-red-700", label: "Dipping — your latest grade is below your average" },
    steady: { icon: Minus, cls: "text-[#8A6D1B]", label: "Holding steady around your average" },
  };

  return (
    <div className="space-y-6 fade-up" data-testid="grade-panel">
      <div className="border border-[#E9DDC6] rounded-md p-6 bg-white shadow-premium flex items-center gap-5">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold border ${gradeColor(data.overall_letter)}`} data-testid="grade-overall">
          {data.overall_letter || "—"}
        </div>
        <div>
          <p className="eyebrow text-[#8A8782] mb-1">Overall grade</p>
          <p className="font-display text-2xl text-[#2C2A28]">
            {data.overall_letter ? `${data.overall_letter} · ${data.overall_points} avg` : "Not graded yet"}
          </p>
          <p className="text-sm text-[#5C5A58]">{data.graded_count} graded {data.graded_count === 1 ? "shoot" : "shoots"}</p>
        </div>
      </div>

      {/* Grade trend over time */}
      {data.graded_count >= 2 && (
        <div className="border border-[#E9DDC6] rounded-md bg-white shadow-premium" data-testid="grade-trend">
          <div className="px-6 py-4 border-b border-[#F0ECE2] flex items-center justify-between">
            <h3 className="font-display text-lg flex items-center gap-2 text-[#2C2A28]">
              <ChartLineUp size={18} weight="duotone" className="text-[#4A0000]" />Grade trend
            </h3>
            {trend && (() => {
              const T = trendMeta[trend];
              return <span className={`flex items-center gap-1.5 text-sm font-medium ${T.cls}`} data-testid="grade-trend-direction"><T.icon size={16} weight="bold" />{trend === "up" ? "Improving" : trend === "down" ? "Dipping" : "Steady"}</span>;
            })()}
          </div>
          <div className="p-6">
            <div className="h-56 w-full" data-testid="grade-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke="#F0ECE2" vertical={false} />
                  <XAxis dataKey="n" tick={{ fill: "#8A8782", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#E9DDC6" }} />
                  <YAxis domain={[0, 4.3]} ticks={[0, 1, 2, 3, 4]} tickFormatter={pointsToLetter}
                    tick={{ fill: "#8A8782", fontSize: 12 }} tickLine={false} axisLine={false} width={34} />
                  <Tooltip content={<TrendTip />} />
                  {data.overall_points != null && (
                    <ReferenceLine y={data.overall_points} stroke="#B08D57" strokeDasharray="4 4"
                      label={{ value: "avg", fill: "#B08D57", fontSize: 11, position: "right" }} />
                  )}
                  <Line type="monotone" dataKey="points" stroke="#4A0000" strokeWidth={2.5}
                    dot={{ r: 4, fill: "#4A0000" }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {trend && <p className="text-xs text-[#5C5A58] mt-3">{trendMeta[trend].label}.</p>}
          </div>
        </div>
      )}

      <div className="border border-[#E9DDC6] rounded-md bg-white shadow-premium">
        <div className="px-6 py-4 border-b border-[#F0ECE2]">
          <h3 className="font-display text-lg flex items-center gap-2 text-[#2C2A28]">
            <Medal size={18} weight="duotone" className="text-[#4A0000]" />Graded shoots
          </h3>
        </div>
        <div className="p-6">
          {data.shoots.length === 0 ? (
            <p className="text-sm text-[#8A8782]">No graded shoots yet. Grades appear here after the Reelty team reviews your delivered work.</p>
          ) : (
            <div className="divide-y divide-[#F0ECE2]" data-testid="grade-shoots">
              {data.shoots.map((s) => (
                <div key={s.property_id} className="flex items-center justify-between py-3 gap-3" data-testid={`grade-shoot-${s.property_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.title}</p>
                    <p className="text-xs text-[#8A8782] truncate">{fmtDate(s.shoot_date)}{s.grade_note ? ` · ${s.grade_note}` : ""}</p>
                  </div>
                  <Badge className={`border shrink-0 ${gradeColor(s.grade)}`}>{s.grade}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
